<template>
  <form
    class="c-form-builder"
    :class="{ '-show-overflow': showOverflow || adjustOverflow }"
    novalidate
    @submit.prevent="onSubmit"
  >
    <c-shadowed
      :shadow-color="shadowColor"
      :has-upper-shadow="!noUpperShadow"
      :has-bottom-shadow="!noBottomShadow"
    >
      <div ref="form-fields" class="form-fields">
        <!-- eslint-disable vue/no-v-for-template-key -->
        <template
          v-for="(fieldSchema, fieldName) in mutatedSchema"
          :key="fieldName"
        >
          <!-- eslint-enable -->
          <div
            v-if="$slots[`${fieldName}-divider`]"
            :class="[ 'divider', `${fieldName}-divider` ]"
          >
            <slot :name="`${fieldName}-divider`" />
          </div>

          <component
            v-bind="$attrs"
            :is="getFieldComponentType(fieldSchema.type)"
            ref="fields"
            :class="[ 'field', fieldSchema.type ]"
            :field-name="fieldName"
            :field-schema="fieldSchema"
            :form-schema="schema"
            :form-value="formData"
            :disabled="disabled"
            :field-options="getFieldOptions(fieldName, fieldSchema)"
            :fields-options="fieldsOptions"
            :label-left="getFieldLabelLeft(fieldSchema)"
            :label-top-desktop-width="labelTopDesktopWidth"
            :actions="fieldSchema.actions || []"
            :model-value="formData[fieldName]"
            :has-validation="hasValidation"
            :errors="errors[fieldSchema.customValidationName || fieldName]"
            @pre-validate="onFieldPreValidate(fieldName, fieldSchema, $event)"
            @list:add="$emit(`list-add:${fieldName}`, $event)"
            @list:remove="$emit(`list-remove:${fieldName}`, $event)"
            @field:action="$emit('field:action', $event)"
            @toggle-preview="onTogglePreview"
            v-on="getFieldHandlers(fieldName, fieldSchema)"
          >
            <template
              v-if="fieldSchema.type === 'slot'"
              v-slot="fieldProps"
            >
              <slot
                :name="`${fieldName}-slot`"
                v-bind="{
                  ...fieldProps,
                  fieldHandlers: getFieldHandlers(fieldName, fieldSchema)
                }"
                v-on="getFieldHandlers(fieldName, fieldSchema)"
              />
            </template>

            <template #under-input>
              <slot :name="`${fieldName}-under-input`" />
            </template>

            <template #aside-input>
              <slot :name="`${fieldName}-aside-input`" />
            </template>
          </component>
        </template>

        <slot />
      </div>

      <slot name="footer" />
    </c-shadowed>

    <div v-show="!noActions" class="actions">
      <slot name="actions">
        <c-button primary class="action">Salvar</c-button>
      </slot>
    </div>
  </form>
</template>

<script>
import { provide } from 'vue'
import { useForm } from 'vee-validate-next'
import CShadowed from '@convenia/components/CShadowed'
import CButton from '@convenia/components/CButton'
import FormField from './fragments/FormField.next.vue'
import FormFieldList from './fragments/FormFieldList.next.vue'
import FormFieldRow from './fragments/FormFieldRow.next.vue'
import SubmitFormat from './mixins/SubmitFormat'
import FormBuilderShared from './mixins/FormBuilderShared'

/**
 * TODO:
 * - Scroll to first error in cases of submission attemps with validation errors
 * - Declarative form syntax
 * - Polish event handlers
 * - Test integration with file upload components
 */

export default {
  name: 'CFormBuilder',

  mixins: [
    SubmitFormat({
      schemaRef: 'schema',
      root: true,
      childRefsName: 'fields'
    }),
    FormBuilderShared({ valueProp: 'modelValue' })
  ],

  components: {
    CShadowed,
    CButton,
    FormFieldList,
    FormFieldRow,
    FormField,
  },

  props: {
    /**
     * The model of the form, contains the initial values for each
     * respective field in the schema.
     */
    modelValue: {
      type: Object,
      required: true
    },

    /**
     * The schema of the value, contains all field definitions
     */
    schema: {
      type: Object,
      required: true
    },

    /**
     * An object containing the options lists for each field,
     * each property will be matched against the appropriate
     * 'optionsSrc' found in the schema of each field.
     */
    fieldsOptions: {
      type: Object,
      default: () => ({})
    },

    /**
     * Whether to remove the default action buttons from the template
     */
    noActions: Boolean,

    /**
     * Whether to disable the entire form
     */
    disabled: Boolean,

    /**
     * Whether to render the fields labels on the left
     * instead of rendering it on the top of the field
     */
    labelLeft: {
      type: Boolean,
      default: false
    },

    /**
     * The width of the input wrapper in the top section of the field
     * in the desktop version when label is on the top
     */
    labelTopDesktopWidth: {
      type: [ Number, String ],
      default: null
    },

    /**
     * Color of CShadowed
     */
    shadowColor: {
      type: String,
      default: '',
    },

    /**
     * No bottom shadow on CShadowed
     */
    noBottomShadow: Boolean,

    /**
     * No upper shadow on CShadowed
     */
    noUpperShadow: Boolean,

    /**
     * Forces all fields to be validated whenever one of the fields
     * is changed (after initial submit event)
     */
    revalidateOnInput: Boolean,

    /**
     * The default behavior of the CShadowed component is to hide
     * all the overflowing content so that the shadows dynamics
     * would work properly.
     *
     * This prop blatantly disables that.
     * Yes. Just like that!
     *
     * Kids these days...
     */
    showOverflow: Boolean,

    /**
     * Whether a form field, especially a select, should allow a null
     * value or just use the default value passed in the schema.
     * Very useful when you have a select field that has a value
     * set up in the schema but also has the nullable property set to true.
     */
    allowNull: {
      type: Boolean,
      default: false
    },

    /**
     * Another way to setup the form required fields.
     * Should be an array of string with the field keys.
     */
    requiredFields: {
      type: Array,
      default: () => ([])
    },

    /**
     * Is available, onRemoveFile will trigger the emit
     * instead of the standar method.
     */
    emitFileRemove: {
      type: Boolean,
      default: false
    },

    /**
     * List of fields errors
     */
    errors: {
      type: Object,
      default: () => ({})
    },

    /**
     * Show errors even if has not submitted
     */
    showErrorsWhenChanged: {
      type: Boolean,
    },

    /**
     * Check in multi-select type if the first item can be removed
     */
    canRemoveFirstItem: {
      type: Boolean,
      default: true
    },
  },

  emits: [
    'update:modelValue',
    'input',
    'submit',
    'invalid-submit',
    'file:remove'
  ],

  setup (props) {
    const { resetForm, validate, validateField } = useForm()

    const resetValidations = () => {
      window.requestAnimationFrame(() => {
        resetForm({
          values: props.modelValue
        })
      })
    }

    provide('validateField', validateField)

    return {
      resetValidations,
      validate
    }
  },

  methods: {
    // Event handler for form components that accept an array as value
    onRemove (fieldName, fieldSchema, index) {
      const field = this.formData[fieldName]
      if (field.length > 1 || this.canRemoveFirstItem) {
        this.formData[fieldName] = [
          ...field.slice(0, index),
          ...field.slice(index + 1)
        ]
      }
    },

    // Event handler for file upload
    onRemoveFile (fieldName, fieldSchema, file) {
      this.formData[fieldName] = this.formData[fieldName].filter(item => item !== file)

      if (!this.emitFileRemove)
        return

      this.$emit('file:remove', file)
    },

    emitFieldChange (fieldName, fieldSchema, value) {
      if (fieldSchema.onInput)
        fieldSchema.onInput(this.formData, value, this)

      if (this.revalidateOnInput && this.hasValidation)
        this.$nextTick(this.validate())

      this.$emit('update:modelValue', this.formData)
      this.$emit(`input:${fieldName}`, value)
    },

    getFieldHandlers (fieldName, fieldSchema) {
      const { handlers: schemaHandlers } = fieldSchema

      const handlers = {
        add: this.onAdd,
        remove: this.onRemove,
        'update:modelValue': this.onInput,
        'input': this.onInput,
        'file:add': this.onAddFile,
        'file:remove': this.onRemoveFile,
      }

      const formHandlers = Object.entries(handlers).reduce((acc, [ evName, evHandler ]) => ({
        ...acc,
        [evName]: value => evHandler(fieldName, fieldSchema, value)
          || this.emitFieldChange(fieldName, fieldSchema, value)
      }), {})

      return {
        ...formHandlers,
        ...(Object.entries(schemaHandlers || {}).reduce((acc, [ evName, evHandler ]) => ({
          ...acc,
          [evName]: evHandler
        }), {}))
      }
    },

    async onSubmit () {
      this.hasValidation = true
      const validation = await this.validate()

      if (validation.valid) {
        const submitData = this.submitFormatEnabled
          ? this.getFormattedFields()
          : this.formData

        this.$emit('submit', submitData)
      } else {
        this.$emit('invalid-submit', this.formData)
        this.focusFirstError()
      }

      // In case this is being called through a ref, would
      // be useful to have this value returned
      return validation.valid
    },
  }
}
</script>

<style lang="scss" src="./style.scss"></style>
